import { ReactNode, useContext } from "react";
import { Navigate } from "react-router-dom";
import { Spin } from "antd";
import { useSelector } from "react-redux";
import AppContext from "./context/AppContext";
import { isEntryProductReachable } from "./routing/guardTriage";
import { findFirstMatchingRoute } from "./routing/redirectPriority";
import { NAVIGATION_ROUTES } from "./utils/NavigationUtils";
import { isTokenExpired } from "./utils/tokenUtils";
import { isPermissionBypassActive } from "./utils/permissionBypass";
import { PAM_TEAM_WHITELIST } from "./utils/Constants";
import { isUserWhitelisted } from "./hooks/useUserWhitelist";
import {
  canBypassGamePassSeasonsViaPam,
  GAME_PASS_SEASONS_PERMISSION,
} from "./utils/gamePassFeature";
import type { RootState } from "./store";

/**
 * Route guard used in `App.js` to gate pages behind authentication and,
 * optionally, a specific admin permission.
 *
 * Auth gate:
 *  - Redirects to the login page if there is no AccessToken, or if the token
 *    expired and there is no RefreshToken to recover with. If the token is
 *    expired but a RefreshToken exists, the 401 interceptor handles refresh.
 *
 * Permission gate (when `requiredPermission` is passed):
 *  - While the admin's profile (and therefore its permissions) is still being
 *    fetched, renders a centered spinner to avoid flickering the page while
 *    the check hasn't resolved.
 *  - Accepts a single permission string or an array of permissions. When an
 *    array is provided the check is an OR: the route is rendered if the admin
 *    holds at least one of the listed permissions. This mirrors the behaviour
 *    of the Sider, where some entries declare multiple `access_role`s.
 *  - If the admin is loaded but does not hold any of the required permissions,
 *    redirects to the highest-priority Sider tab the admin CAN access
 *    (via `REDIRECT_PRIORITY` in `src/routing/redirectPriority.ts`) instead
 *    of `/users/home`. That keeps an admin who, say, only holds
 *    `casino.reports.providers` from being bounced into `/users/home` (which
 *    they also can't access) when they hit a route they lack permission for.
 *  - When the admin holds no Sider-recognised permission at all, falls back
 *    to `NAVIGATION_ROUTES.LOGIN`. `RedirectIfAuthenticated` on `/` then
 *    clears the staff session and renders the login form — the same
 *    "session is broken" handling used at sign-in time. This avoids the
 *    redirect loop that would happen if we fell back to `/users/home` for
 *    an admin who lacks the `users` permission.
 *
 * Break-glass override:
 *  - Appending `?bypassPermissions=true` to any URL skips the permission gate
 *    for the rest of the tab's session (sticky in `sessionStorage`). The auth
 *    gate is NOT bypassed — a valid token is still required. See
 *    `src/utils/permissionBypass.ts` for the full rationale and limits.
 *
 * Usage:
 *   <RequireAuth>
 *     <SomePublicToEveryAdminPage />
 *   </RequireAuth>
 *
 *   <RequireAuth requiredPermission="sportsbook.monitoring.sgp_risk">
 *     <SgpRiskPage />
 *   </RequireAuth>
 *
 *   <RequireAuth
 *     requiredPermission={["sportsbook.configuration.sportsbook_fm", "configuration.sportsbook_fm"]}
 *   >
 *     <SportsbookFmPage />
 *   </RequireAuth>
 *
 * The permission string follows the same dot-separated catalog used by the
 * Sider access_role fields (see `src/utils/constants/permissionsConstants.js`
 * and `docs/components/sider/sidebar-entries-visibility.md`).
 */
type StoredToken = {
  AccessToken?: string;
  RefreshToken?: string;
} | null;

// Local view of the `AppContext` shape — only the fields this guard needs.
// `AppContext` itself lives in a .js file and is created without a generic,
// so we narrow it here instead of widening the whole context.
type AppContextShape = {
  adminPermissions: string[] | null;
  adminDataLoaded: boolean;
};

type RequireAuthProps = {
  children: ReactNode;
  requiredPermission?: string | string[];
  /** Allows whitelisted PAM team access to the GA Seasons route when
   *  `additional_features.game_pass_pam` is on, even before granular
   *  permissions ship on the admin role. Scoped to Seasons only. */
  allowGamePassPamAccess?: boolean;
};

const RequireAuth = ({
  children,
  requiredPermission,
  allowGamePassPamAccess = false,
}: RequireAuthProps) => {
  const { adminPermissions, adminDataLoaded, adminEmail, adminUsername } =
    useContext(AppContext) as AppContextShape & {
      adminEmail?: string | null;
      adminUsername?: string | null;
    };
  const featuresStatusConfig = useSelector(
    (state: RootState) => state.config?.appConfig?.features_status_config,
  );

  const rawToken = localStorage.getItem("token");
  let token: StoredToken = null;
  try {
    token = rawToken ? (JSON.parse(rawToken) as StoredToken) : null;
  } catch {
    // Malformed token in localStorage (corrupted storage, old format, or
    // an injected value). Treat as absent so the guard redirects to login
    // instead of crashing the tree with a SyntaxError.
    token = null;
  }

  const shouldRedirectToLogin =
    !token?.AccessToken || (isTokenExpired() && !token?.RefreshToken);

  if (shouldRedirectToLogin) {
    return <Navigate to={NAVIGATION_ROUTES?.LOGIN} replace />;
  }

  // Normalize to an array; an empty array means "no permission required".
  const requiredPermissions = Array.isArray(requiredPermission)
    ? requiredPermission
    : requiredPermission
      ? [requiredPermission]
      : [];

  if (requiredPermissions.length === 0) {
    return <>{children}</>;
  }

  // Break-glass override: when the bypass query param/sessionStorage flag is
  // active, render children without consulting `adminPermissions` — also
  // skipping the loading spinner, since by definition we don't care about the
  // permission list anymore.
  if (isPermissionBypassActive()) {
    return <>{children}</>;
  }

  if (!adminDataLoaded) {
    return (
      <div
        data-testid="require-auth-loading"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  const hasPermission =
    Array.isArray(adminPermissions) &&
    requiredPermissions.some((permission) =>
      adminPermissions.includes(permission)
    );
  const hasGamePassPamAccess =
    allowGamePassPamAccess &&
    requiredPermissions.includes(GAME_PASS_SEASONS_PERMISSION) &&
    canBypassGamePassSeasonsViaPam({
      featuresStatusConfig,
      isPamTeamWhitelisted: isUserWhitelisted(PAM_TEAM_WHITELIST, {
        adminEmail,
        adminUsername,
      }),
    });

  if (!hasPermission && !hasGamePassPamAccess) {
    // Reachable-first, then unrestricted (deterministic when all-blocked/pre-init).
    const fallbackRoute =
      findFirstMatchingRoute(adminPermissions, {
        isEntryReachable: isEntryProductReachable,
      }) ??
      findFirstMatchingRoute(adminPermissions) ??
      NAVIGATION_ROUTES?.LOGIN;
    return <Navigate to={fallbackRoute} replace />;
  }

  return <>{children}</>;
};

export default RequireAuth;
